import { useId, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { BlTextButton } from "Components/Shared/Buttons/BlTextButton";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { Resources, useResource } from "Translations/Resources";

/** Validation both siblings share; wrappers add their own checks (e.g. video duration) on top. */
export type MediaUploadIssue = "invalidType" | "tooLarge";

/**
 * Drops bypass the picker's accept filter entirely, so both selection paths must validate — and a
 * rejected file must say why instead of silently doing nothing.
 */
export const getMediaUploadIssue = (
  file: File,
  acceptedTypes: ReadonlyArray<string>,
  maxFileSizeMb: number,
): MediaUploadIssue | null => {
  if (!acceptedTypes.includes(file.type)) {
    return "invalidType";
  }
  if (file.size > maxFileSizeMb * 1024 * 1024) {
    return "tooLarge";
  }
  return null;
};

/**
 * Uploads are stored under a tick-stamped name, so after a reload the stored name is all we have;
 * the wrappers prefer the picked file's own name while they still know it.
 */
export const getFileNameFromUrl = (url: string): string =>
  url.split("?")[0].split("/").pop() ?? url;

type Props = {
  label: string;
  /** Explanatory copy under the label, e.g. what the slot is for. */
  hint?: string;
  acceptedTypes: ReadonlyArray<string>;
  isBusy: boolean;
  /** Preview of the uploaded value; the empty prompt renders when null. */
  preview: React.ReactNode | null;
  /** Shown next to the preview. */
  fileName?: string;
  emptyIcon: React.ReactNode;
  promptText: string;
  /** Wrapper-owned Alert elements rendered under the dropzone. */
  alerts: React.ReactNode;
  /** Raw file from the picker or a drop — the wrapper validates and uploads. */
  onFile: (file: File) => void;
  /** Clears the uploaded value; the blob itself is kept (story media is never deleted). */
  onClear: () => void;
};

/**
 * Type-blind drag-and-drop / click-to-select dropzone shared by the story media upload fields —
 * it owns only the selection mechanics; what a valid file is, how it uploads and how it previews
 * belongs to the wrapping field.
 */
export const MediaUploadField = ({
  label,
  hint,
  acceptedTypes,
  isBusy,
  preview,
  fileName,
  emptyIcon,
  promptText,
  alerts,
  onFile,
  onClear,
}: Props) => {
  const { t } = useResource();
  const inputId = useId();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFile(file);
    }
    // Re-selecting the same file must fire change again (e.g. retrying after a failure).
    e.target.value = "";
  };

  // A drop mid-upload would start a second upload racing the first, so it is ignored.
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && !isBusy) {
      onFile(file);
    }
  };

  const openPicker = () => document.getElementById(inputId)?.click();

  const fileInput = (
    <input
      id={inputId}
      type="file"
      accept={acceptedTypes.join(",")}
      onChange={handleFileSelect}
      hidden
    />
  );

  return (
    <Stack gap={1}>
      <div>
        <StyledInputLabel htmlFor={inputId}>{label}</StyledInputLabel>
        {hint && (
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        )}
      </div>
      {preview !== null && !isBusy ? (
        <Stack direction="row" alignItems="center" gap={3} flexWrap="wrap">
          {fileInput}
          <Box
            width={160}
            borderRadius={1}
            overflow="hidden"
            bgcolor="common.black"
            sx={{ aspectRatio: "9 / 16" }}
          >
            {preview}
          </Box>
          <Stack gap={2} minWidth={0}>
            {fileName && (
              <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                {fileName}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={openPicker}
              >
                {t(Resources.Common.Replace)}
              </Button>
              <BlTextButton color="error" onClick={onClear}>
                {t(Resources.Common.Remove)}
              </BlTextButton>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(!isBusy);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={openPicker}
          sx={{
            border: "1px dashed",
            borderColor: isDragOver ? "primary.main" : "divider",
            borderRadius: 1,
            p: 2,
            textAlign: "center",
            bgcolor: isDragOver ? "action.hover" : "transparent",
            transition: "all 0.2s",
            cursor: "pointer",
          }}
        >
          {fileInput}
          {isBusy ? (
            <CircularProgress size={32} />
          ) : (
            <Stack alignItems="center" gap={0.5} py={2}>
              {emptyIcon}
              <Typography variant="body2" color="text.secondary">
                {promptText}
              </Typography>
            </Stack>
          )}
        </Box>
      )}
      {alerts}
    </Stack>
  );
};
